import {useDispatch, useSelector} from 'react-redux';
import {Tooltip} from 'react-tooltip';

import {isNull} from 'lodash';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createSearchParams, Link, useNavigate, useParams} from 'react-router-dom';
import {apiUrl, gradeApi} from '../../../api.js';
import {useAuth} from '../../../auth/AuthContext';
import {formatDateTime} from "../../DueTimeDisplayTable";
import {useQueryParams} from '../../../shared/hooks/use-query-params.tsx';
import {socket} from '../../../socket.js';
import {ArrowLeft, ArrowRight, ArrowUp} from 'lucide-react';

import {
    clearAssignmentStudents,
    getAssignmentStudents,
    getLogs
} from '../../../store/teacherSlice.js';
import {DEFAULT_PAGE_NUMBER, ITEMS_PER_PAGE} from '../../../utils/pagination.ts';
import ConfirmationModal from '../../ConfirmationModal';
import AIRegrade from './AIRegrade';
import {PDFView} from "../PDFRendered";

import SUnpublishedFlexibleAssignment
    from '../../Student/SUnpublishedFlexibleAssignment/SUnpublishedFlexibleAssignment';
import StudentSearchDropdown from "../TeacherAssignments/TeacherAssignmentSettings/StudentSearchDropdown";

import {BrainIcon, Check, UserCircleIcon} from 'lucide-react';


import {
    PencilIcon,
    SparklesIcon,
    TrashIcon,
    PlusCircleIcon,
    XMarkIcon,
    ChevronDownIcon,
    ChevronUpIcon
} from '@heroicons/react/24/outline'
import {ExclamationCircleIcon} from "@heroicons/react/16/solid";
import {LaTeXPreviewContent} from "../../LaTeXPreviewContent";

// Truncated Text component for expandable text display
const TruncatedText = ({text, maxLength = 70, className = ''}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) {
        return null;
    }

    const needsTruncation = text.length > maxLength;
    const displayText = isExpanded || !needsTruncation ? text : `${text.substring(0, maxLength)}...`;

    return (
        <div className={`${className} flex flex-wrap items-start`}>
            <div className="whitespace-pre-wrap flex-grow">{displayText}</div>
            {needsTruncation && (
                <button
                    className="ml-2 text-[#e4b17a] hover:text-[#c99b69] text-sm font-medium flex items-center transition-all duration-200 whitespace-nowrap flex-shrink-0"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    {isExpanded ? (
                        <>
                            <span>Show less</span>
                            <ChevronUpIcon className="w-4 h-4 ml-1"/>
                        </>
                    ) : (
                        <>
                            <span>Show more</span>
                            <ChevronDownIcon className="w-4 h-4 ml-1"/>
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

const TDetails = props => {
    const dispatch = useDispatch();

    const {user} = useAuth();
    const {assignment_id, submission_id} = useParams();
    const [submissionData, setSubmissionData] = useState({});
    const [examPaper, setExamPaper] = useState([]);
    const [feedbackItems, setFeedbackItems] = useState({});
    const [editableFeedback, setEditableFeedback] = useState({});
    const [message, setMessage] = useState('');
    const [isEditingAIFeedback, setIsEditingAIFeedback] = useState({});
    const [editableAIFeedback, setEditableAIFeedback] = useState({});
    const aiFeedbackRefs = useRef({});
    const [assignment, setAssignment] = useState(null);
    const [studentName, setStudentName] = useState('');
    const navigate = useNavigate();
    const [isRegradeOpen, setIsRegradeOpen] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [regradeData, setRegradeData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmissionLoading, setIsSubmissionLoading] = useState(false);
    const [activeSubQuestionId, setActiveSubQuestionId] = useState(null);
    const [isReviewed, setIsReviewed] = useState(false);
    const [assignedStudentId, setAssignedStudentId] = useState(null);
    const [error, setError] = useState(null);
    const [isStudentAssignmentConfirmationOpen, setIsStudentAssignmentConfirmationOpen] =
        useState(false);
    const [newStudentAssignment, setNewStudentAssignment] = useState(null);
    const [regradeInProgress, setRegradeInProgress] = useState({});
    const pollingInterval = useRef(null);
    const [gradePending, setGradePending] = useState(false);
    const [groupStudents, setGroupStudents] = useState([]);
    const [regradeRequests, setRegradeRequests] = useState([]);
    const [nextRegrade, setNextRegrade] = useState(-1);
    const [showSubmissionLog, setShowSubmissionLog] = useState(false);
    const regradeRef = useRef(null);
    const [isReloadMessageShown, setIsReloadMessageShown] = useState(false);

    // Pre-fetching state
    const [prefetchedSubmissions, setPrefetchedSubmissions] = useState({});
    const [prefetchingSubmissionId, setPrefetchingSubmissionId] = useState(null);
    const [showNavTips, setShowNavTips] = useState(true);
    // Track current PDF page - initialize from localStorage if available
    const [currentPdfPage, setCurrentPdfPage] = useState(() => {
        const savedPage = localStorage.getItem('currentPdfPage');
        // Make sure we have a valid number, defaulting to 1 if not
        const initialPage = savedPage && !isNaN(parseInt(savedPage, 10)) ? parseInt(savedPage, 10) : 1;
        console.log('Initial PDF page loaded from localStorage:', initialPage);
        return initialPage;
    });

    // Track current rubric item (question/subquestion) for scroll persistence
    const [currentRubricItem, setCurrentRubricItem] = useState(() => {
        const savedItem = localStorage.getItem('currentRubricItem');
        return savedItem || null;
    });

    // Track the exact scroll position of the rubric panel
    const [rubricScrollPosition, setRubricScrollPosition] = useState(() => {
        const savedPosition = localStorage.getItem('rubricScrollPosition');
        return savedPosition ? parseInt(savedPosition, 10) : 0;
    });

    // Also track scroll position as a percentage
    const [rubricScrollPercentage, setRubricScrollPercentage] = useState(() => {
        const savedPercentage = localStorage.getItem('rubricScrollPercentage');
        return savedPercentage ? parseFloat(savedPercentage) : 0;
    });

    // Reference to the right panel (rubric container)
    const rightPanelRef = useRef(null);

    // Ref map to store references to all rubric item elements
    const rubricItemRefs = useRef({});

    // Key for forcing PDFView to reinitialize with the correct page
    const [pdfViewKey, setPdfViewKey] = useState(0);

    // Track whether we've already restored the scroll position for the current submission
    const [hasRestoredScrollPosition, setHasRestoredScrollPosition] = useState(false);

    // Function to handle rubric panel scrolling and save position
    const handleRubricScroll = useCallback(() => {
        // Only save scroll position if we've already restored it first
        // This prevents overwriting our saved position during the initial load
        if (hasRestoredScrollPosition && rightPanelRef.current) {
            const scrollElement = rightPanelRef.current;
            const scrollTop = scrollElement.scrollTop;
            const scrollHeight = scrollElement.scrollHeight;
            const clientHeight = scrollElement.clientHeight;

            // Calculate scroll percentage (0 to 1)
            const scrollPercentage = scrollHeight > clientHeight
                ? scrollTop / (scrollHeight - clientHeight)
                : 0;

            console.log('Saving rubric scroll percentage:', scrollPercentage.toFixed(4), 'Raw position:', scrollTop);

            // Save both percentage and absolute position
            localStorage.setItem('rubricScrollPercentage', String(scrollPercentage));
            localStorage.setItem('rubricScrollPosition', String(scrollTop));
            setRubricScrollPosition(scrollTop);
        }
    }, [hasRestoredScrollPosition]);

    // Reset PDF view when the submission changes
    useEffect(() => {
        console.log('TDetails component mounted, submission_id:', submission_id);
        const savedPage = localStorage.getItem('currentPdfPage');
        if (savedPage) {
            console.log('Found saved PDF page:', savedPage);
            // Force PDFView to reinitialize with the saved page
            setPdfViewKey(prevKey => prevKey + 1);
        }
    }, [submission_id]);

    // Track previous submission id to detect changes
    const prevSubmissionIdRef = useRef(submission_id);

    // Apply the saved rubric scroll position, but only when changing submissions
    useEffect(() => {
        // Only reset the flag when submission actually changes
        if (prevSubmissionIdRef.current !== submission_id) {
            console.log('Submission changed, will restore scroll position');
            setHasRestoredScrollPosition(false);
            prevSubmissionIdRef.current = submission_id;
        }

        // Exit immediately if we've already restored position for this submission
        if (hasRestoredScrollPosition) {
            console.log('Scroll position already restored, allowing free scrolling');
            return;
        }

        // Function to apply the scroll position once
        const applyScrollPosition = (attempt = 1, maxAttempts = 3) => {
            if (!rightPanelRef.current) {
                return;
            }

            const scrollElement = rightPanelRef.current;

            // 1. Try percentage-based scrolling first (more reliable with varying content heights)
            if (rubricScrollPercentage > 0) {
                const scrollHeight = scrollElement.scrollHeight;
                const clientHeight = scrollElement.clientHeight;
                const calculatedPosition = Math.round(rubricScrollPercentage * (scrollHeight - clientHeight));

                console.log(`Attempt ${attempt}: Applying percentage-based scroll (${rubricScrollPercentage.toFixed(4)}) → position ${calculatedPosition}`);
                scrollElement.scrollTop = calculatedPosition;
            }
            // 2. Fall back to absolute position if percentage is not available
            else if (rubricScrollPosition > 0) {
                console.log(`Attempt ${attempt}: Applying absolute scroll position: ${rubricScrollPosition}`);
                scrollElement.scrollTop = rubricScrollPosition;
            }
            // 3. Fall back to item-based scrolling if neither position method works
            else if (currentRubricItem && rubricItemRefs.current[currentRubricItem]) {
                console.log(`Attempt ${attempt}: Falling back to rubric item scroll: ${currentRubricItem}`);
                rubricItemRefs.current[currentRubricItem].scrollIntoView({behavior: 'smooth'});

                // Even with item-based scrolling, mark as restored
                setHasRestoredScrollPosition(true);
                return; // Don't continue with further attempts when using item scrolling
            }

            // Mark as restored after the attempt so user can freely scroll
            if (attempt >= maxAttempts) {
                console.log('Max attempts reached, marking scroll as restored');
                setHasRestoredScrollPosition(true);
            }
            // Otherwise make another attempt with increasing delay
            else {
                setTimeout(() => {
                    // Only proceed with the next attempt if we haven't marked as restored yet
                    if (!hasRestoredScrollPosition) {
                        applyScrollPosition(attempt + 1, maxAttempts);
                    }
                }, attempt * 200);
            }
        };

        // Start the first attempt after a short delay to ensure content begins loading
        const initialTimer = setTimeout(() => {
            // Explicitly check again to prevent race conditions
            if (!hasRestoredScrollPosition) {
                applyScrollPosition(1);
                // Set this after the first attempt to ensure we never keep trying
                setTimeout(() => setHasRestoredScrollPosition(true), 600);
            }
        }, 300);

        return () => {
            clearTimeout(initialTimer);
        };
    }, [submission_id, rubricScrollPosition, rubricScrollPercentage, currentRubricItem, hasRestoredScrollPosition]);
    const [assignmentDetails, setAssignmentDetails] = useState(null);
    const [teachers, setTeachers] = useState(null);
    const [showAllReviewedPopup, setShowAllReviewedPopup] = useState(false);

    // reset
    const [socketApi, setSocketApi] = useState(socket(user.session_token));
    const [isConnected, setIsConnected] = useState(false);
    const [prevSubmissionId, setPrevSubmissionId] = useState(submission_id);

    const [showConnectionErrorPopup, setShowConnectionErrorPopup] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [studentToDelete, setStudentToDelete] = useState(null);

    const {data: students, isLoading: isAssignmentStudentsLoading} = useSelector(
        state => state?.teacherReducer?.assignmentStudents
    );

    const {data: logs, isLoading: isLogsLoading} = useSelector(
        state => state?.teacherReducer?.logs
    );

    const {
        params: {search, pageNumber: scrollPageNumber, itemsPerPage},
        updateSearchParams
    } = useQueryParams({
        search: '',
        pageNumber: DEFAULT_PAGE_NUMBER,
        itemsPerPage: ITEMS_PER_PAGE.default
    });

    useEffect(() => {
        updateSearchParams({
            search: '',
            pageNumber: DEFAULT_PAGE_NUMBER
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        socketApi.on('connect', () => {
            console.log('connected');
            setIsConnected(true);
            setShowConnectionErrorPopup(false);

            socketApi.emit('assignment-join', {
                assignmentId: assignment_id
            });
        });

        socketApi.on('connect_error', err => {
            setShowConnectionErrorPopup(true);
            setTimeout(() => {
                socketApi.connect();
            }, 1000);
        });

        return () => {
            socketApi.emit('assignment-leave', {
                assignmentId: assignment_id
            });

            socketApi.disconnect();
            socketApi.on('disconnect', () => {
                console.log('disconnected');
                setIsConnected(false);
                setShowConnectionErrorPopup(false);
            });
        };
    }, []);

    useEffect(() => {
        if (isConnected) {
            socketApi.emit('submission-join', {
                fullName: `${user.user.first_name} ${user.user.last_name}`,
                submissionId: submission_id,
                id: user.user.id,
                assignmentId: assignment_id
            });
            setPrevSubmissionId(submission_id);
        }
    }, [submission_id, isConnected]);

    useEffect(() => {
        if (isConnected) {
            socketApi.on('submission-teachers', data => {
                setTeachers(data);
                console.log(data);
            });

            socketApi.on('assignment-details', data => {
                setAssignmentDetails(data);
                console.log(data, 'assignment-details');
            });

            socketApi.on('submission-refresh', data => {
                console.log(data, 'submission-refresh');

                if (user.user.id !== data.id) {
                    setIsReloadMessageShown(true);
                    setTimeout(() => setIsReloadMessageShown(false), 2000);

                    getQuestions();
                    getAssignmentSubmission();
                    getAssignment();
                }
            });

            socketApi.on('errors', data => {
                console.error(data);
            });
        }

        return () => {
            socketApi.removeAllListeners();
        };
    }, [isConnected]);

    useEffect(() => {
        getRegrades();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.regrade_question_id]);

    useEffect(() => {
        if (assignment?.is_exam && assignment.course_id && submissionData.user) {
            getStudents();
        }

        return () => dispatch(clearAssignmentStudents());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignment, submissionData, scrollPageNumber, itemsPerPage]);

    useEffect(() => {
        if (props.regrade_question_id && regradeRef.current) {
            const timer = setTimeout(() => {
                regradeRef.current?.scrollIntoView({behavior: 'smooth'});
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [props.regrade_question_id, examPaper, regradeRef]);

    useEffect(() => {
        fetchGroupStudents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignment]);

    // Combined effect for fetching assignment and submission data
    // Main data loading effect with safeguards against infinite loops
    useEffect(() => {
        let isMounted = true;

        // Only run this effect if we have valid IDs and component is still mounted
        if (assignment_id && submission_id && isMounted) {
            // Use an async function to load data
            const loadData = async () => {
                try {
                    await getQuestions();
                    await getAssignmentSubmission();
                    await getAssignment();
                    if (isMounted) {
                        startPolling();
                    }
                } catch (error) {
                    console.error('Error loading data:', error);
                }
            };

            loadData();
        }

        // Clean up function
        return () => {
            isMounted = false;
            stopPolling();
        };
    }, [assignment_id, submission_id]);

    // Ensure we don't trigger effects on initial render with an empty dependency array
    useEffect(() => {
        // This useEffect runs only once when the component mounts
        console.log('TDetails component mounted');

        return () => {
            console.log('TDetails component unmounted');
        };
    }, []);

    // Add keyboard event listener for arrow navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Ignore if focus is in an input, textarea, or other form element
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
                return;
            }

            // Left arrow - go to previous submission
            if (event.key === 'ArrowLeft') {
                clickRight();
            }
            // Right arrow - go to next submission
            else if (event.key === 'ArrowRight') {
                clickLeft();
            }
            // Up arrow - scroll rubric to top and PDF to first page
            else if (event.key === 'ArrowUp') {
                // Scroll rubric panel to top
                if (rightPanelRef.current) {
                    rightPanelRef.current.scrollTop = 0;
                    // Save the scroll position
                    localStorage.setItem('rubricScrollPosition', '0');
                    localStorage.setItem('rubricScrollPercentage', '0');
                    setRubricScrollPosition(0);
                    setRubricScrollPercentage(0);
                }

                // Set PDF to first page
                setCurrentPdfPage(1);
                localStorage.setItem('currentPdfPage', '1');

                // Force PDFView to reinitialize with the first page
                setPdfViewKey(prevKey => prevKey + 1);

                console.log('Up arrow pressed: Reset rubric scroll position and PDF to page 1');
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [submission_id]);

    useEffect(() => {
        if (assignedStudentId) {
            getAllLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignedStudentId]);

    const socketSubmissionUpdate = () =>
        socketApi.emit('submission-update', {
            submissionId: submission_id,
            assignmentId: assignment_id,
            id: user.user.id
        });

    const getRegrades = async () => {
        try {
            const response = await gradeApi.get({
                path: `/api/regrade-requests/`,
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });
            const regradesForTeacher = response.data;
            console.log(regradesForTeacher);
            setRegradeRequests(regradesForTeacher);
            const currentIndex = regradesForTeacher
                .map(item => {
                    return item.id;
                })
                .indexOf(Number(submission_id));
            const feedback = regradesForTeacher[currentIndex]?.feedback;
            var nextFeedback = -1;
            var found = false;
            for (const feedbackItem in feedback) {
                if (found && feedback[feedbackItem].regrade_request) {
                    nextFeedback = feedbackItem;
                    setNextRegrade(nextFeedback);
                    break;
                }
                if (feedbackItem == props.regrade_question_id) {
                    found = true;
                }
            }

        } catch (error) {
            console.log(error);
        }
    };

    // Fetch submission data - can be used for pre-fetching or getting current submission
    const fetchSubmission = async (submissionId) => {
        if (!submissionId || !assignment_id) {
            return null;
        }

        try {
            const response = await gradeApi.get({
                path: `/api/assignment/${assignment_id}/submission/${submissionId}/`,
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching submission:', error);
            return null;
        }
    };

    // Pre-fetch submissions for smoother navigation
    const prefetchSubmission = useCallback(async (submissionId) => {
        if (!submissionId || prefetchingSubmissionId === submissionId || prefetchedSubmissions[submissionId]) {
            return;
        }

        setPrefetchingSubmissionId(submissionId);

        try {
            const data = await fetchSubmission(submissionId);
            if (data) {
                setPrefetchedSubmissions(prev => ({
                    ...prev,
                    [submissionId]: data
                }));
            }
        } finally {
            setPrefetchingSubmissionId(null);
        }
    }, [assignment_id, user.session_token, prefetchingSubmissionId, prefetchedSubmissions]);

    // Effect to pre-fetch next and previous submissions when current submission loads
    // Uses a ref to track if we've already prefetched for the current submission
    const hasPrefetchedForCurrentSubmission = useRef(false);

    useEffect(() => {
        // Skip prefetching if:
        // 1. We don't have submission IDs
        // 2. There's only one submission
        // 3. We don't have a current submission ID
        // 4. We've already prefetched for this submission
        if (!props.submissionIds ||
            props.submissionIds.length <= 1 ||
            !submission_id ||
            hasPrefetchedForCurrentSubmission.current) {
            return;
        }

        // Mark that we've prefetched for this submission to prevent re-fetching
        hasPrefetchedForCurrentSubmission.current = true;

        // Add a delay to avoid potential race conditions with other effects
        const prefetchTimer = setTimeout(() => {
            const currentIndex = props.submissionIds.indexOf(Number(submission_id));
            if (currentIndex === -1) {
                return;
            }

            // Calculate next and previous indices
            const nextIndex = (currentIndex + 1) % props.submissionIds.length;
            const prevIndex = (currentIndex - 1 + props.submissionIds.length) % props.submissionIds.length;

            // Pre-fetch next submission first (prioritize), then previous
            const nextSubmissionId = props.submissionIds[nextIndex];
            const prevSubmissionId = props.submissionIds[prevIndex];

            // Only prefetch if not already in cache
            if (nextSubmissionId && !prefetchedSubmissions[nextSubmissionId]) {
                console.log('Pre-fetching next submission:', nextSubmissionId);
                prefetchSubmission(nextSubmissionId);
            }

            // Wait a bit before fetching the previous one to prioritize network resources
            const prevFetchTimer = setTimeout(() => {
                if (prevSubmissionId && !prefetchedSubmissions[prevSubmissionId]) {
                    console.log('Pre-fetching previous submission:', prevSubmissionId);
                    prefetchSubmission(prevSubmissionId);
                }
            }, 1000);

            return () => clearTimeout(prevFetchTimer);
        }, 800); // Increased delay for better stability

        return () => {
            clearTimeout(prefetchTimer);
            // Reset the flag when the component unmounts or submission changes
            hasPrefetchedForCurrentSubmission.current = false;
        };
    }, [submission_id]);

    // Fetching assignment submission details
    const getAssignmentSubmission = async () => {
        setIsSubmissionLoading(true);

        try {
            // Check if we already have pre-fetched data for this submission
            if (prefetchedSubmissions[submission_id]) {
                // Use the pre-fetched data
                const data = prefetchedSubmissions[submission_id];
                setSubmissionData(data);
                setFeedbackItems(data?.submitted ? data?.feedback : {});
                setIsReviewed(data.is_reviewed);
                setAssignedStudentId(data.user.id);
                setStudentName(`${data.user.first_name} ${data.user.last_name}`);

                // Remove from pre-fetched cache to save memory
                setPrefetchedSubmissions(prev => {
                    const {[submission_id]: _, ...rest} = prev;
                    return rest;
                });
            } else {
                // Fetch the data if not pre-fetched
                const response = await gradeApi.get({
                    path: `/api/assignment/${assignment_id}/submission/${submission_id}/`,
                    config: {
                        headers: {
                            Authorization: `Bearer ${user.session_token}`
                        }
                    }
                });
                const data = response.data;

                setSubmissionData(data);
                setFeedbackItems(data?.submitted ? data?.feedback : {});
                setIsReviewed(data.is_reviewed);
                setAssignedStudentId(data.user.id);
                setStudentName(`${data.user.first_name} ${data.user.last_name}`);
            }
        } catch (error) {
            setMessage(error.response?.data?.error || 'Error fetching submission');
        } finally {
            setIsSubmissionLoading(false);
        }
    };

    const getStudents = async () => {
        await dispatch(
            getAssignmentStudents({
                classId: assignment.course_id,
                token: user.session_token,
                search,
                pageNumber: Number(scrollPageNumber),
                itemsPerPage: Number(itemsPerPage)
            })
        );

        setAssignedStudentId(submissionData.user.id);
    };

    const getAllLogs = async () => {
        await dispatch(
            getLogs({
                userId: assignedStudentId,
                token: user.session_token,
                assignmentId: assignment_id
            })
        );
    };

    const handleConfirmStudentAssignment = async () => {
        try {
            await gradeApi.post({
                path: `/api/assignment/${assignment_id}/submissions/${submission_id}/assign-student/`,
                body: {new_student_id: newStudentAssignment.id},
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });

            setAssignedStudentId(newStudentAssignment.id);
            setStudentName(newStudentAssignment.full_name);

            // Refresh the submission data
            getAssignmentSubmission();
            setIsStudentAssignmentConfirmationOpen(false);
        } catch (error) {
            console.error('Failed to assign student:', error);
            setError('Failed to assign student. Please try again.');
        }
    };

    // Handle deleting student submissions
    const handleDeleteSubmission = () => {
        console.log('Handling deletion, assignedStudentId:', assignedStudentId); // Debug log
        // Ensure we have a valid student ID (use the user ID from submissionData if available)
        const studentId = assignedStudentId || submissionData?.user?.id;
        if (!studentId) {
            setError('Cannot delete: No student ID found for this submission');
            return;
        }
        setStudentToDelete(studentId);
        setShowDeleteModal(true);
    };

    // Confirm deletion of student submissions
    const confirmDeleteSubmission = async () => {
        try {
            if (!studentToDelete) {
                return;
            }

            console.log('Deleting submission for student ID:', studentToDelete);

            await gradeApi.post({
                path: `/api/assignment/${assignment_id}/delete-student-submissions/`,
                body: {
                    student_id: studentToDelete
                },
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });

            // Close the modal
            setShowDeleteModal(false);
            setStudentToDelete(null);
            setError('');

            // Navigate to next submission using the same function as the Next button
            clickLeft();
        } catch (error) {
            console.error('Error deleting student submissions:', error);
            setError('Failed to delete student submissions. Please try again.');
        }
    };

    // Close delete modal
    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setStudentToDelete(null);
    };

    const fetchGroupStudents = async () => {
        try {
            if (!assignedStudentId) {
                return;
            }
            const response = await gradeApi.get({
                path: `/api/assignment/get_students/${assignment.id}/student/${assignedStudentId}`, // Make sure to use assignment.id, not aid
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });
            setGroupStudents(response.data); // Assuming response.data is an array
        } catch (error) {
            console.error('Error fetching group students:', error);
            // Consider setting an error state here instead of using setError
        }
    };

    const handleMarkReviewed = async () => {
        try {
            const newReviewedStatus = !isReviewed;
            const response = await gradeApi.post({
                path: `/api/assignment/${assignment_id}/submissions/${submission_id}/mark-reviewed/`,
                body: {is_reviewed: newReviewedStatus},
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });
            setIsReviewed(newReviewedStatus);
            socketSubmissionUpdate();
        } catch (error) {
            console.error('Failed to update review status:', error);
            setMessage('Failed to update review status.');
        }
    };

    const getIsDisabledGradingButton = subQuestionId => {
        return !!assignmentDetails?.activeSubmissionsQuestionRegradeRooms.find(
            obj => obj.id !== user.user.id && obj.questionId === subQuestionId
        );
    };

    const startPolling = () => {
        if (!pollingInterval.current) {
            pollingInterval.current = setInterval(getQuestions, 5000);
        }
    };

    const stopPolling = () => {
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
            // Removed window.location.reload() which was causing infinite reloads
        }
    };

    const getQuestions = async () => {
        try {
            const response = await gradeApi.get({
                path: `/api/assignment/${assignment_id}/questions/`,
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });
            const questionsFromDB = response.data;

            if (pollingInterval.current) {
                // If we're polling, only update subquestions that are being regraded
                setExamPaper(prevExamPaper =>
                    prevExamPaper.map(question => ({
                        ...question,
                        subQuestions: question.subQuestions.map(subQuestion => {
                            // Find corresponding subquestion from API response
                            const updatedSubQuestion = questionsFromDB.questions
                                .flatMap(q => q.subQuestions)
                                .find(sq => sq.id === subQuestion.id);

                            // Only update if this subquestion is being regraded
                            if (regradeInProgress[subQuestion.id]) {
                                return updatedSubQuestion || subQuestion;
                            }
                            return subQuestion;
                        })
                    }))
                );
            } else {
                // If not polling, update everything as normal
                setExamPaper(questionsFromDB.questions);
            }

            // Check for regrade_in_progress
            const newRegradeStatus = {};
            questionsFromDB.questions.forEach(question => {
                question.subQuestions.forEach(subQuestion => {
                    if (subQuestion.regrade_in_progress) {
                        newRegradeStatus[subQuestion.id] = true;
                    }
                });
            });
            setRegradeInProgress(newRegradeStatus);

            // Start or stop polling based on regrade status
            if (Object.values(newRegradeStatus).some(status => status)) {
                startPolling();
            } else {
                stopPolling();
            }
        } catch (error) {
            console.log(error);
        }
    };

    // Fetching assignment details
    const getAssignment = async () => {
        try {
            const response = await gradeApi.get({
                path: `/api/assignment/${assignment_id}/only/`,
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });
            setAssignment(response.data);
        } catch (error) {
            setMessage('Failed to fetch assignment details');
        }
    };

    // Update student name when submission data changes
    useEffect(() => {
        if (submissionData.user) {
            setStudentName(`${submissionData.user.first_name} ${submissionData.user.last_name}`);
        }
    }, [submissionData]);

    const clickRight = () => {
        if (!props.submissionIds || props.submissionIds.length === 0) {
            console.error('No submissions available for navigation');
            return;
        }

        // Save the current PDF page and rubric item before navigation
        localStorage.setItem('currentPdfPage', String(currentPdfPage));
        if (currentRubricItem) {
            localStorage.setItem('currentRubricItem', currentRubricItem);
        }

        const currentIndex = props.submissionIds.indexOf(Number(submission_id));
        let prevIndex =
            currentIndex === -1
                ? props.submissionIds.length - 1
                : (currentIndex - 1 + props.submissionIds.length) % props.submissionIds.length;

        navigateToSubmission(prevIndex, -1);
    };

    const clickUnseen = () => {
        let prevIndex;
        if (!props.submissionUnseenIds || props.submissionUnseenIds.length === 0) {
            console.error('No submissions available for navigation');
            return;
        }

        // Save the current PDF page and rubric item before navigation
        localStorage.setItem('currentPdfPage', String(currentPdfPage));
        if (currentRubricItem) {
            localStorage.setItem('currentRubricItem', currentRubricItem);
        }

        const availableIds = props.submissionUnseenIds.filter(
            id => !assignmentDetails?.activeSubmissionsRooms.includes(String(id))
        );

        const currentIndex = props.submissionUnseenIds.indexOf(Number(submission_id));

        if (currentIndex === availableIds.length || currentIndex === -1) {
            prevIndex = 0;
        } else if (currentIndex !== -1 && currentIndex < availableIds.length) {
            prevIndex = currentIndex;
        } else {
            prevIndex = null;
        }

        navigateToSubmission(prevIndex, -1, true, availableIds);
    };

    const clickLeft = () => {
        if (!props.submissionIds || props.submissionIds.length === 0) {
            console.error('No submissions available for navigation');
            return;
        }

        // Save the current PDF page and rubric item before navigation
        localStorage.setItem('currentPdfPage', String(currentPdfPage));
        if (currentRubricItem) {
            localStorage.setItem('currentRubricItem', currentRubricItem);
        }

        const currentIndex = props.submissionIds.indexOf(Number(submission_id));
        let nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % props.submissionIds.length;

        navigateToSubmission(nextIndex, 1);
    };

    const clearEditableFeedback = () => {
        setEditableFeedback({});
    };

    const navigateToSubmission = (startIndex, direction, isUnseenOnly = false, availableIds) => {
        // Create safe local copies of the search params with fallbacks to avoid initialization errors
        const searchTerm = typeof search !== 'undefined' ? search : '';
        const pageNum = typeof scrollPageNumber !== 'undefined' ? scrollPageNumber : DEFAULT_PAGE_NUMBER;
        const itemsCount = typeof itemsPerPage !== 'undefined' ? itemsPerPage : ITEMS_PER_PAGE.default;

        // Guard clause - if there's only one submission, don't navigate
        if (props.submissionIds.length === 1) {
            return;
        }

        // Clear any editable feedback before navigation
        clearEditableFeedback();

        let currentIndex = startIndex;
        const ids = isUnseenOnly ? availableIds : props.submissionIds;
        const maxAttempts = ids.length;

        // If we don't have a valid index or no submissions, show message and return to submissions list
        if (isNull(currentIndex) || maxAttempts === 0) {
            setShowAllReviewedPopup(true);
            setTimeout(() => {
                setShowAllReviewedPopup(false);
                navigate({
                    pathname: `/tclasses/${props.class_id}/tassignments/${assignment_id}/tsubmissions`,
                    search: `?${createSearchParams({
                        search: searchTerm,
                        pageNumber: pageNum,
                        itemsPerPage: itemsCount
                    })}`
                });
            }, 3000);
            return;
        }

        // Find the next valid submission
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const nextSubmissionId = ids[currentIndex];
            if (nextSubmissionId !== null) {
                // Handle regrade case
                if (props.regrade_question_id) {
                    for (const request in regradeRequests) {
                        if (regradeRequests[request].id === nextSubmissionId) {
                            let subquestion_id = null;
                            for (const subquestion in regradeRequests[request].feedback) {
                                if (regradeRequests[request].feedback[subquestion].regrade_request) {
                                    subquestion_id = subquestion;
                                    break;
                                }
                            }

                            // Create path for navigation
                            const pathname = `/tclasses/${regradeRequests[request].assignment.course.id}/tassignments/${regradeRequests[request].assignment.id}/tsubmissions/${nextSubmissionId}/tdetails/${subquestion_id}`;

                            // Use the safe local copies for search parameters
                            navigate({
                                pathname,
                                search: `?${createSearchParams({
                                    search: searchTerm,
                                    pageNumber: pageNum,
                                    itemsPerPage: itemsCount
                                })}`
                            }, {replace: true});
                            return;
                        }
                    }
                } else {
                    // Standard navigation - no regrade
                    const pathname = `/tclasses/${props.class_id}/tassignments/${assignment_id}/tsubmissions/${nextSubmissionId}/tdetails`;

                    // Use the safe local copies for search parameters
                    navigate({
                        pathname,
                        search: `?${createSearchParams({
                            search: searchTerm,
                            pageNumber: pageNum,
                            itemsPerPage: itemsCount
                        })}`
                    }, {replace: true});
                    return;
                }
            }
            // Move to the next index in the specified direction
            currentIndex = (currentIndex + direction + ids.length) % ids.length;
        }

        console.error('No valid submissions found to navigate to');
    };
    const hasRubricItem = (subquestionId, rubricId) => {
        if (!feedbackItems) {
            return false;
        }
        if (!subquestionId) {
            return false;
        }
        if (subquestionId in feedbackItems) {
            let rubricItems = feedbackItems[subquestionId]?.rubric_items;
            for (let i = 0; i < rubricItems.length; i++) {
                if (rubricItems[i].id === rubricId) {
                    return true;
                }
            }
        }
        return false;
    };

    const getFeedback = subquestionId => {
        if (!feedbackItems) {
            return '';
        }
        if (subquestionId in feedbackItems) {
            return feedbackItems[subquestionId].feedback;
        }
        return '';
    };

    const getInstructorFeedback = subquestionId => {
        if (!feedbackItems) {
            return '';
        }
        if (subquestionId in feedbackItems) {
            return feedbackItems[subquestionId].instructor_text;
        }
        return '';
    };

    const getRR = subquestionId => {
        if (!feedbackItems) {
            return '';
        }
        if (subquestionId in feedbackItems) {
            return feedbackItems[subquestionId].regrade_request;
        }
        return '';
    };

    const getRRText = subquestionId => {
        if (!feedbackItems) {
            return '';
        }
        if (subquestionId in feedbackItems) {
            return feedbackItems[subquestionId].regrade_text;
        }
        return '';
    };

    const handleRubricItemChange = (subquestionId, rubricItem) => () => {
        // Simple API call to update the rubric item
        gradeApi
            .post({
                path: `/api/assignment/${assignment_id}/submissions/${submission_id}/grade/`,
                body: {
                    subquestionId: subquestionId,
                    rubric: rubricItem
                },
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            })
            .then(response => {
                if (response && response.data) {
                    // Simply update the feedback item with the new rubric
                    setFeedbackItems(prevFeedbackItems => {
                        const newFeedbackItems = {...prevFeedbackItems};

                        // Just update the rubric_items for this specific subquestion
                        if (newFeedbackItems[subquestionId]) {
                            newFeedbackItems[subquestionId] = {
                                ...newFeedbackItems[subquestionId],
                                rubric_items: [rubricItem] // Directly use the rubric item we just sent
                            };
                        }

                        return newFeedbackItems;
                    });

                    // Update grade if provided
                    if (response.data.grade !== undefined) {
                        setSubmissionData(prev => ({...prev, grade: response.data.grade}));
                    }

                    // Notify other connected users
                    socketSubmissionUpdate();
                }
                setGradePending(false);
            })
            .catch(error => {
                console.error("Error updating rubric item:", error);
                setGradePending(false);
            });
    };

    const isEditing = subquestionId => editableFeedback.hasOwnProperty(subquestionId);

    const handleRegradeReq = subquestionId => {
        if (subquestionId in feedbackItems) {
            handleRegradeReqDel(subquestionId);
        }
    };

    const handleRegradeReqDel = subquestionId => {
        const submissionID = submissionData.id;

        gradeApi
            .post({
                path: `/api/assignment/${assignment_id}/submissions/${submissionID}/regrade-request/`,
                body: {
                    subquestionId: subquestionId,
                    regradeRequest: false
                },
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            })
            .catch(error => {
                console.log('Error updating regrade request:', error);
            })
            .then(response => {
                if (response) {
                    setFeedbackItems(prevFeedbackItems => ({
                        ...prevFeedbackItems,
                        [subquestionId]: {
                            ...prevFeedbackItems[subquestionId],
                            regrade_request: false
                        }
                    }));
                }
            });
    };

    const renderFeedback = feedback => {
        return (
            <div className="mb-2">
                <LaTeXPreviewContent content={feedback}/>
            </div>
        );
    };


    const handleRegradeClick = subQuestionId => {
        setActiveSubQuestionId(subQuestionId);
        setIsRegradeOpen(true);

        socketApi.emit('submission-question-regrade-join', {
            submissionId: submission_id,
            id: user.user.id,
            assignmentId: assignment_id,
            questionId: subQuestionId
        });
    };

    const handleRegradeClose = () => {
        setIsRegradeOpen(false);
        setActiveSubQuestionId(null);

        socketApi.emit('submission-question-regrade-leave', {
            submissionId: submission_id,
            id: user.user.id,
            assignmentId: assignment_id,
            questionId: activeSubQuestionId
        });

        // window.location.reload();
    };

    const handleRemoveFeedback = (subquestionId) => {
        gradeApi
            .post({
                path: `/api/assignment/${assignment_id}/submissions/${submission_id}/feedback/`,
                body: {
                    subquestionId: subquestionId,
                    feedback: ""
                },
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            })
            .catch(error => {
                console.log('Error removing AI feedback:', error);
            })
            .then(response => {
                if (response) {
                    setFeedbackItems(prevFeedbackItems => ({
                        ...prevFeedbackItems,
                        [subquestionId]: {
                            ...prevFeedbackItems[subquestionId],
                            feedback: ""
                        }
                    }));
                    socketSubmissionUpdate();
                }
            });
    };

    const handleEditAIFeedback = (subquestionId) => {
        // Get existing feedback or initialize with empty string for new feedback
        const currentFeedback = getFeedback(subquestionId) || '';

        // Set up the editing state for this specific subquestion
        setEditableAIFeedback(prev => ({
            ...prev,
            [subquestionId]: currentFeedback
        }));

        setIsEditingAIFeedback(prev => ({
            ...prev,
            [subquestionId]: true
        }));

        // Focus on the textarea after it renders
        setTimeout(() => {
            if (aiFeedbackRefs.current[subquestionId]) {
                aiFeedbackRefs.current[subquestionId].focus();
            }
        }, 0);
    };

    const handleAIFeedbackChange = (subquestionId, value) => {
        setEditableAIFeedback(prev => ({
            ...prev,
            [subquestionId]: value
        }));
    };


// Function to handle saving AI feedback
    const handleAIFeedbackSave = (subquestionId) => {
        gradeApi
            .post({
                path: `/api/assignment/${assignment_id}/submissions/${submission_id}/ai-feedback/`,
                body: {
                    subquestionId: subquestionId,
                    feedback: editableAIFeedback[subquestionId]
                },
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            })
            .catch(error => {
                console.log('Error updating AI feedback:', error);
            })
            .then(response => {
                if (response) {
                    // Update the state with the new/updated feedback
                    setFeedbackItems(prevFeedbackItems => {
                        // Check if we have an existing entry for this subquestion
                        const existingItem = prevFeedbackItems[subquestionId];

                        return {
                            ...prevFeedbackItems,
                            [subquestionId]: {
                                // If this is a new feedback item, initialize it properly
                                // Otherwise, preserve existing data and just update the feedback field
                                ...(existingItem || {is_viewed: true, rubric_items: []}),
                                ...prevFeedbackItems[subquestionId],
                                feedback: editableAIFeedback[subquestionId]
                            }
                        };
                    });

                    // For new feedback, update the submission with the response data
                    // This ensures we get all the updated data from the backend
                    if (response.data && response.data.feedback) {
                        setSubmissionData(prevData => ({
                            ...prevData,
                            feedback: response.data.feedback
                        }));
                    }

                    // Notify other connected clients about the update
                    socketSubmissionUpdate();

                    // Clear the editing state
                    setIsEditingAIFeedback(prev => {
                        const {[subquestionId]: _, ...rest} = prev;
                        return rest;
                    });

                    setEditableAIFeedback(prev => {
                        const {[subquestionId]: _, ...rest} = prev;
                        return rest;
                    });

                    socketSubmissionUpdate();
                }
            });
    };

// Function to cancel AI feedback editing
    const handleAIFeedbackCancel = (subquestionId) => {
        setIsEditingAIFeedback(prev => {
            const {[subquestionId]: _, ...rest} = prev;
            return rest;
        });

        setEditableAIFeedback(prev => {
            const {[subquestionId]: _, ...rest} = prev;
            return rest;
        });
    };

// Function to check if AI feedback is being edited
    const checkEditingAIFeedback = (subquestionId) =>
        isEditingAIFeedback.hasOwnProperty(subquestionId);
    const handleRegradeSubmit = (
        newRubric,
        regradeInstructions,
        regradeFeedbackInstructions,
        solution
    ) => {
        setRegradeData({newRubric, regradeInstructions, regradeFeedbackInstructions, solution});
        setIsConfirmationOpen(true);
    };

    const handleConfirmRegrade = async () => {
        setIsConfirmationOpen(false);
        setIsLoading(true);
        try {
            const response = await gradeApi.post({
                path: `/api/assignment/${assignment_id}/ai-regrade/`,
                body: {
                    subQuestionId: activeSubQuestionId,
                    newRubric: regradeData.newRubric,
                    regradeInstructions: regradeData.regradeInstructions,
                    regradeFeedbackInstructions: regradeData.regradeFeedbackInstructions,
                    solution: regradeData.solution
                },
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });
            // console.log('AI-Regrade initiated:', response.data);
            getQuestions(); // Fetch the updated questions
            socketApi.emit('submission-question-regrade-leave', {
                submissionId: submission_id,
                id: user.user.id,
                assignmentId: assignment_id,
                questionId: activeSubQuestionId
            });
            // Instead of reload, we'll fetch the updated data
            getQuestions();
            getAssignmentSubmission();
            setMessage('AI-Regrade initiated. The system will update the grades shortly.');
        } catch (error) {
            console.error('AI-Regrade failed:', error);
            setMessage('AI-Regrade failed. Please try again.');
        } finally {
            setIsLoading(false);
            setIsRegradeOpen(false);
            setActiveSubQuestionId(null);
        }
    };

    const SortRubricItemsAndDisplay = ({subQuestion}) => {
        subQuestion.rubricItems.sort((a, b) => {
            if (a.value == b.value) {
                return 0;
            }
            return a.value > b.value ? -1 : 1;
        });

        return (
            <div className="rounded-xl overflow-hidden border border-gray-300">
                {subQuestion.rubricItems.map((rubricItem, rubricIndex) => (
                    <div
                        key={rubricIndex}
                        className={`
                    flex items-center px-3 py-2 cursor-pointer
                    ${
                            hasRubricItem(subQuestion.id, rubricItem.id)
                                ? 'bg-orange-200'
                                : 'bg-white hover:bg-gray-50'
                        }
                    ${rubricIndex !== 0 ? 'border-t border-gray-300' : ''}
                `}
                        onClick={handleRubricItemChange(subQuestion.id, rubricItem)}
                    >
                        <div className="flex items-center flex-1">
                            <div
                                className={`
                        w-5 h-5 rounded-full border border-gray-400 
                        flex items-center justify-center mr-3
                        ${
                                    hasRubricItem(subQuestion.id, rubricItem.id)
                                        ? 'bg-gray-500 border-gray-700'
                                        : ''
                                }
                    `}
                            >
                                {hasRubricItem(subQuestion.id, rubricItem.id) && (
                                    <Check className="w-3 h-3 text-white stroke-2"/>
                                )}
                            </div>
                            <div className="flex items-center">
                                <span className="text-xl font-medium mr-3">{rubricItem.value}</span>
                                <div className="text-sm">
                                    <LaTeXPreviewContent content={rubricItem.descriptor}/>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const handleScroll = () => {
        const currentIndex = props.submissionIds.indexOf(Number(submission_id));
        navigate(
            `/tclasses/${props.class_id}/tassignments/${assignment_id}/tsubmissions/${props.submissionIds[currentIndex]}/tdetails/${nextRegrade}`
        );
    };

    return (
        <div
            key={`${assignment_id}-${submission_id}`}
            className="flex flex-col w-full h-screen overflow-hidden"
        >
            {/* navigation trail */}
            <div className="flex flex-col fixed w-full pb-5 pt-10 bg-white px-5 mb-2 z-50">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                        <Link to="/tclasses" className="text-black text-lg font-medium ml-4">
                            My Classes
                        </Link>
                        <Link to={`/tclasses/${assignment?.course_id}/tassignments`}>
                            <button>
                                <img src="/assets/back_arrow.png" className="size-6" alt="Back"/>
                            </button>
                        </Link>
                        <Link
                            to={`/tclasses/${assignment?.course_id}/tassignments`}
                            className="text-black text-lg font-medium ml-4"
                        >
                            {assignment?.course?.name.length > 40
                                ? assignment?.course?.name.substring(0, 37) + '...'
                                : assignment?.course?.name}
                        </Link>
                        <Link
                            to={`/tclasses/${assignment?.course_id}/tassignments/${assignment?.id}/tsubmissions`}
                        >
                            <button>
                                <img src="/assets/back_arrow.png" className="size-6" alt="Back"/>
                            </button>
                        </Link>
                        <Link
                            to={`/tclasses/${assignment?.course_id}/tassignments/${assignment?.id}/tsubmissions`}
                            className="text-black text-lg font-medium ml-4"
                        >
                            {assignment?.title.length > 40
                                ? assignment?.title.substring(0, 37) + '...'
                                : assignment?.title}
                        </Link>
                        <Link
                            to={`/tclasses/${assignment?.course_id}/tassignments/${assignment?.id}/tsubmissions`}
                        >
                            <img src="/assets/back_arrow.png" className="size-6" alt="Back"/>
                        </Link>
                        <div className="flex items-center">
                            <h1 className="text-black text-lg font-medium ml-4">
                                {studentName ? (studentName.length > 20 ? studentName.substring(0, 17) + '...' : studentName) : ''}
                            </h1>
                        </div>
                    </div>

                    {isReloadMessageShown && (
                        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded">
                            Change detected, reloading
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        {teachers &&
                            teachers.entities?.length > 0 &&
                            (teachers?.entities?.length <= 3 ? (
                                (teachers?.entities ?? []).map(t => (
                                    <div key={t.id} className="relative flex items-center space-x-2 ml-2">
                    <span
                        id={`teacher${t.id}`}
                        className={`relative cursor-pointer text-lg w-7 h-7 rounded-full border border-gray-400 
                            flex items-center justify-center p-4 ${t.id === user.user.id ? 'border-orange-200' : 'border-gray-400'}`}
                    >
                      {t.fullName[0].toUpperCase()}
                    </span>
                                        <Tooltip anchorSelect={`#teacher${t.id}`} place="bottom">
                                            {t.fullName}
                                        </Tooltip>
                                    </div>
                                ))
                            ) : (
                                <>
                                    {(teachers?.entities?.slice(0, 3) ?? []).map(t => (
                                        <div key={t.id} className="relative flex items-center space-x-2 ml-2">
                      <span
                          id={`teacher${t.id}`}
                          className={`relative cursor-pointer text-lg w-7 h-7 rounded-full border border-gray-400 
                            flex items-center justify-center p-4 ${t.id === user.user.id ? 'border-orange-200' : 'border-gray-400'}`}
                      >
                        {t.fullName[0].toUpperCase()}
                      </span>
                                            <Tooltip anchorSelect={`#teacher${t.id}`} place="bottom">
                                                {t.fullName}
                                            </Tooltip>
                                        </div>
                                    ))}
                                    <div className="relative flex items-center space-x-2 ml-2">
                    <span
                        id="teachers-hidden"
                        className={`relative cursor-pointer text-lg w-7 h-7 rounded-full border border-gray-400 flex items-center justify-center p-4`}
                    >
                      ...
                    </span>
                                        <Tooltip anchorSelect="#teachers-hidden" place="bottom">
                                            {(teachers?.entities?.slice(3) ?? []).map(t => (
                                                <div key={t.id} className="flex gap-2 items-center justify-center">
                                                    <p>{t.fullName}</p>
                                                    {t.role === 'owner' ? (
                                                        <span
                                                            className="px-4 inline-flex text-xs leading-4 font-semibold rounded-full bg-red-100 text-red-800">
                              I
                            </span>
                                                    ) : (
                                                        <span
                                                            className="px-4 inline-flex text-xs leading-4 font-semibold rounded-full bg-green-100 text-green-800">
                              C
                            </span>
                                                    )}
                                                </div>
                                            ))}
                                        </Tooltip>
                                    </div>
                                </>
                            ))}
                        <div className="flex items-center space-x-2 ml-5">
                            <button
                                className="text-gray-600 text-l font-medium flex items-center"
                                onClick={clickRight}
                            >
                                <span>Back</span>
                                <i className="fas fa-sort-up -rotate-90 text-2xl ml-3"></i>
                            </button>

                            {/* Submission counter showing current position / total */}
                            <div className="px-4 py-2 bg-gray-100 rounded-lg mx-2 flex items-center justify-center">
                                <span className="font-medium text-gray-800">
                                    {props.submissionIds && props.submissionIds.length > 0 ? (
                                        `${props.submissionIds.indexOf(Number(submission_id)) + 1}/${props.submissionIds.length}`
                                    ) : (
                                        '1/1'
                                    )}
                                </span>
                            </div>

                            <button
                                className="text-gray-600 text-l font-medium flex items-center"
                                onClick={clickLeft}
                            >
                                <i className="fas fa-sort-up rotate-90 text-2xl mr-3"></i>
                                <span>Next</span>
                            </button>

                        </div>

                    </div>
                </div>
            </div>

            {showAllReviewedPopup && (
                <>
                    {/* Blurred background overlay */}
                    <div
                        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
                        style={{backdropFilter: 'blur(4px)'}}
                    />

                    {/* Popup */}
                    <div className="fixed inset-0 flex items-center justify-center z-[99]">
                        <div
                            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 transform transition-all animate-fade-in">
                            <div className="flex items-center">
                                <div className="bg-orange-100 rounded-full p-3 mr-4">
                                    <img src="/assets/checkmark.png" className="size-6" alt="Success"/>
                                </div>
                                <p className="text-lg font-medium">
                                    All submissions reviewed. Taking you to the submissions list.
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* main content */}
            <div
                className="flex fixed left-8 w-[calc(100%-1rem)] bottom-0 top-0 h-[calc(100vh-7rem)] mt-[7rem] overflow-y-hidden">
                {/* student work */}
                <div className="w-1/2 p-4 h-full overflow-hidden flex flex-col">
                    {submissionData?.submitted && (
                        submissionData.essay_text ? (
                            <div className="w-full h-full overflow-auto bg-white border border-gray-300 p-4 rounded-xl">
                                <pre className="whitespace-pre-wrap font-sans">{submissionData.essay_text}</pre>
                            </div>
                        ) : submissionData.assignment?.type === 'flexible_problem_set' ? (
                            <div
                                className="bg-white h-full border border-gray-200 rounded-lg shadow-sm p-6 overflow-auto">
                                <SUnpublishedFlexibleAssignment
                                    isReadMode
                                    assignment_id={submissionData.assignment.id}
                                    assignment={submissionData.assignment}
                                    class_id={props.class_id}
                                    studentId={submissionData.user.id}
                                />
                            </div>
                        ) : (
                            <div className="w-full h-full border border-gray-300 rounded-xl overflow-hidden">
                                <div className="w-full h-full relative">
                                    <PDFView
                                        key={`pdf-view-${submission_id}-${pdfViewKey}`}
                                        url={`${apiUrl}/api/pdf-file/${submissionData.file.filename}`}
                                        className="absolute inset-0"
                                        initialPage={currentPdfPage - 1} // Convert from 1-indexed to 0-indexed
                                        onPageChange={(pageIndex) => {
                                            console.log('Direct page index received:', pageIndex);

                                            // The react-pdf-viewer library directly passes the page index (0-based)
                                            // Add 1 to convert to a 1-based page number that's more user-friendly
                                            const pageNumber = pageIndex + 1;
                                            console.log('Converted to page number:', pageNumber);

                                            // Store the page number immediately when it changes
                                            setCurrentPdfPage(pageNumber);
                                            localStorage.setItem('currentPdfPage', String(pageNumber));
                                        }}
                                    />
                                </div>
                            </div>
                        )
                    )}
                </div>

                <div ref={rightPanelRef} className="w-1/2 overflow-y-auto p-8" onScroll={handleRubricScroll}>
                    {assignment?.is_exam && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned to:</label>
                            <div className="flex w-full space-x-2">
                                <div className="flex-grow">
                                    <StudentSearchDropdown
                                        search={search}
                                        isLoading={isAssignmentStudentsLoading}
                                        pageNumber={Number(scrollPageNumber)}
                                        itemsPerPage={itemsPerPage}
                                        students={students}
                                        currentStudentName={studentName}
                                        updateSearchParams={updateSearchParams}
                                        onStudentSelect={student => {
                                            setNewStudentAssignment(student);
                                            setIsStudentAssignmentConfirmationOpen(true);
                                        }}
                                    />
                                </div>
                                <button
                                    className="h-10 px-3 py-2 bg-red-50 hover:bg-red-100 border border-red-200 rounded flex items-center text-red-600 self-end transition-colors"
                                    title="Delete all submissions for this student"
                                    onClick={handleDeleteSubmission}
                                >
                                    <TrashIcon className="h-5 w-5 mr-1.5"/>
                                    <span className="text-sm font-medium">Delete</span>
                                </button>
                            </div>
                            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                        </div>
                    )}
                    {/* Score and Late Submission Section */}
                    <div className="flex flex-col">
                        <h2 className="text-3xl font-bold mb-4">
                            Score: {submissionData?.grade}/{assignment?.max_score}
                        </h2>

                        {submissionData?.late && assignment?.course?.late_gets_zero && (
                            <div className="mb-6">
                                <div className="text-red-600 font-medium mb-2">
                                    This submission is late, so a score of 0 was assigned.
                                </div>
                                <button
                                    className=" px-4 py-2 rounded-3xl border-[1px] border-black text-smallish font-medium transition-all duration-300 desktop:hover:-translate-y-[0.5px] desktop:hover:shadow bg-orange-200 hover:bg-orange-300 text-black"
                                    onClick={async () => {
                                        try {
                                            await gradeApi.patch({
                                                path: `/api/assignment/${assignment_id}/submissions/${submission_id}/approve-late/`,
                                                config: {
                                                    headers: {
                                                        Authorization: `Bearer ${user.session_token}`
                                                    }
                                                }
                                            });
                                            // Refresh submission data after approval
                                            await getAssignmentSubmission();
                                        } catch (error) {
                                            console.error('Error approving late submission:', error);
                                            setError('Failed to approve late submission');
                                        }
                                    }}
                                >
                                    Approve Late Submission
                                </button>
                            </div>
                        )}
                    </div>
                    {/* Submission Log Section */}
                    <div className="flex flex-col w-full mb-5">
                        <button
                            type="button"
                            className="flex items-center text-sm mb-2"
                            onClick={() => setShowSubmissionLog(!showSubmissionLog)}
                        >
                            <img
                                src="/assets/back_arrow.png"
                                className={`size-5 opacity-60 transform ${showSubmissionLog ? 'rotate-90' : '-rotate-90'} mr-2`}
                                alt="Toggle submission log"
                            />
                            Submission Log
                        </button>
                    </div>

                    {showSubmissionLog && (
                        <div className="flex flex-col w-full">
                            {logs?.entities?.length > 0 ? (
                                logs.entities.map((log, index) => (
                                    <div key={index} className="mb-2 ml-4">
                                        <p className="text-sm text-gray-700">
                                            {logs.totalCount - index}. Submitted at{' '}
                                            {formatDateTime(log.submissionDate, user.user.timezone)} by{' '}
                                            <span
                                                id={`user${log.firstName + log.userId}`}
                                                className="relative cursor-pointer hover:text-gray-900"
                                            >
                        <strong>{log.fullName}</strong>
                      </span>
                                        </p>
                                        <Tooltip anchorSelect={`#user${log.firstName + log.userId}`} place="bottom">
                                            <p>ID: {log.userId}</p>
                                            <p>Email: {log.email}</p>
                                        </Tooltip>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-700 ml-4">No submissions made yet.</p>
                            )}
                        </div>
                    )}
                    {groupStudents.length > 0 && (
                        <div>
                            <p>Group Members:</p>
                            <p>
                                {Array.isArray(groupStudents) && groupStudents.length > 0
                                    ? groupStudents.map((student, index) => (
                                        <span key={index}>
                        {student[0]} ({student[1]}){index < groupStudents.length - 1 ? ', ' : ''}
                      </span>
                                    ))
                                    : 'No students to display'}
                            </p>
                        </div>
                    )}
                    {examPaper.map((question, questionIndex) => (
                        <div key={questionIndex} className="mb-8">
                            {question.subQuestions.map((subQuestion, subQuestionIndex) => {
                                const isDisabledGrading = getIsDisabledGradingButton(subQuestion.id);
                                const itemProps = subQuestion.id == props.regrade_question_id ? {ref: regradeRef} : {};
                                return (
                                    <div
                                        key={subQuestionIndex}
                                        className={`mb-6 ml-4 ${regradeInProgress[subQuestion.id] ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                                        {...itemProps}
                                        ref={el => {
                                            // Create a unique ID for this rubric item
                                            const itemId = `q-${questionIndex}-sub-${subQuestionIndex}`;
                                            // Store the ref in our map
                                            rubricItemRefs.current[itemId] = el;
                                            // Update current item when it becomes visible in viewport
                                            const observer = new IntersectionObserver(
                                                (entries) => {
                                                    entries.forEach(entry => {
                                                        if (entry.isIntersecting) {
                                                            setCurrentRubricItem(itemId);
                                                        }
                                                    });
                                                },
                                                {threshold: 0.5} // Element is at least 50% visible
                                            );
                                            if (el) {
                                                observer.observe(el);
                                            }
                                            return () => {
                                                if (el) {
                                                    observer.unobserve(el);
                                                }
                                            };
                                        }}
                                    >
                                        {regradeInProgress[subQuestion.id] && (
                                            <div
                                                className="mt-2 mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-700 rounded">
                                                ⏳ This subquestion is being regraded...
                                            </div>
                                        )}

                                        {/* Question header styling */}
                                        {assignment?.type === 'flexible_problem_set' ? (
                                            <>
                                                <h2 className="text-xl font-semibold mb-2 text-gray-700">{`Question ${questionIndex + 1}:`}</h2>

                                                {/* Main question text */}
                                                {question.description && (
                                                    <div
                                                        className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                        <TruncatedText
                                                            text={question.description}
                                                            className="text-gray-700 leading-relaxed"
                                                            maxLength={70}
                                                        />
                                                    </div>
                                                )}

                                                {!question.is_without_sub_questions && (
                                                    <div className="mb-3">
                                                        <h3 className="font-medium mb-1 text-gray-700">
                                                            {`${String.fromCharCode(97 + subQuestionIndex)})`}
                                                        </h3>
                                                        <TruncatedText
                                                            text={subQuestion.question}
                                                            className="ml-5 text-gray-700"
                                                            maxLength={70}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {/* Main question with text on the same line */}
                                                {subQuestionIndex === 0 && (
                                                    <div className="mb-4">
                                                        <div className="flex items-baseline mb-1">
                                                            <span
                                                                className="text-gray-700 mr-2 font-medium text-lg">{`${questionIndex + 1}) `}</span>
                                                            {question.description ? (
                                                                <TruncatedText
                                                                    text={question.description}
                                                                    className="text-gray-700 font-medium text-lg"
                                                                    maxLength={70}
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Subquestion with consistent styling - check if it's the only subquestion with blank text */}
                                                <div className="mb-3">
                                                    <div className="flex items-baseline mb-1">
                                                        {/* If there's only one subquestion with blank text, don't show the subquestion letter */}
                                                        <span className="text-gray-700 mr-2">
                                                            {question.subQuestions.length === 1 && !subQuestion.question ?
                                                                `` :
                                                                `${questionIndex + 1}${String.fromCharCode(97 + subQuestionIndex)}) `
                                                            }
                                                        </span>
                                                        {subQuestion.question && (
                                                            <TruncatedText
                                                                text={subQuestion.question}
                                                                className="text-gray-700"
                                                                maxLength={70}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {/* Essay question styling */}
                                        {assignment?.type === 'essay' && (
                                            <div className="mb-2">
                                                <TruncatedText
                                                    text={subQuestion.question}
                                                    className="font-medium text-gray-700"
                                                    maxLength={70}
                                                />
                                            </div>
                                        )}

                                        {/* Rubric section */}
                                        <div className="flex flex-col gap-2 mb-2 mt-3">
                                            <SortRubricItemsAndDisplay subQuestion={subQuestion}/>
                                        </div>

                                        {/* Feedback section */}
                                        <div className="mt-4">
                                            {!isEditing(subQuestion.id) && (
                                                <>
                                                    {/* AI Feedback Section - conditionally show based on whether feedback exists */}
                                                    {/* If editing mode is active, show the edit interface regardless of whether feedback exists */}
                                                    {checkEditingAIFeedback(subQuestion.id) ? (
                                                        <div
                                                            className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <h4 className="font-medium text-gray-600 text-sm uppercase tracking-wide flex items-center">
                                                                    <BrainIcon className="w-5 h-5 mr-2"/>
                                                                    {getFeedback(subQuestion.id) ? 'Edit Feedback' : 'Add Feedback'}
                                                                </h4>
                                                                <button
                                                                    type="button"
                                                                    className="hover:text-red-600 transition-colors"
                                                                    title="Cancel"
                                                                    onClick={() => handleAIFeedbackCancel(subQuestion.id)}
                                                                >
                                                                    <XMarkIcon className="w-5 h-5"/>
                                                                </button>
                                                            </div>
                                                            <div className="space-y-3">
                                                                <textarea
                                                                    ref={el => (aiFeedbackRefs.current[subQuestion.id] = el)}
                                                                    value={editableAIFeedback[subQuestion.id]}
                                                                    className="w-full p-3 border border-[rgba(228,177,121,0.2)] rounded-lg focus:ring-2 focus:ring-[rgba(228,177,121,0.3)] focus:border-[#e4b17a]"
                                                                    rows="6"
                                                                    placeholder="Enter feedback here..."
                                                                    onChange={e => handleAIFeedbackChange(subQuestion.id, e.target.value)}
                                                                />
                                                                <div className="flex justify-end gap-3 mt-2">
                                                                    <button
                                                                        className="px-4 py-2 rounded-lg bg-gradient-to-br from-[#e4b17a] to-[#c99b69] text-white text-sm font-medium hover:from-[#c99b69] hover:to-[#b38a5e] transition-all duration-200 shadow-sm"
                                                                        onClick={() => handleAIFeedbackSave(subQuestion.id)}
                                                                    >
                                                                        Save Changes
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        /* When not in editing mode, show existing feedback or the Add Feedback button */
                                                        getFeedback(subQuestion.id) ? (
                                                            <>
                                                                <div
                                                                    className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                                    <div
                                                                        className="flex justify-between items-start mb-2">
                                                                        <h4 className="font-medium text-gray-600 text-sm uppercase tracking-wide flex items-center">
                                                                            <BrainIcon className="w-5 h-5 mr-2"/>
                                                                            Feedback
                                                                        </h4>
                                                                        <div className="flex space-x-2">
                                                                            <button
                                                                                className="hover:text-amber-600 transition-colors"
                                                                                title="Edit AI feedback"
                                                                                onClick={() => handleEditAIFeedback(subQuestion.id)}
                                                                            >
                                                                                <PencilIcon className="w-5 h-5"/>
                                                                            </button>
                                                                            <button
                                                                                className="hover:text-red-700 transition-colors"
                                                                                title="Remove AI feedback"
                                                                                onClick={() => handleRemoveFeedback(subQuestion.id)}
                                                                            >
                                                                                <TrashIcon className="w-5 h-5"/>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-gray-700 leading-relaxed">
                                                                        {renderFeedback(getFeedback(subQuestion.id))}
                                                                    </div>
                                                                </div>

                                                                {/* Fine Tune Grading Button when feedback exists */}
                                                                <div className="mb-4 flex justify-end">
                                                                    <button
                                                                        disabled={isDisabledGrading}
                                                                        className={`px-4 py-2 flex items-center gap-2 ${
                                                                            'text-amber-600 hover:bg-amber-50'
                                                                        } rounded-lg transition-colors ${isDisabledGrading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                        onClick={() => {
                                                                            handleRegradeClick(subQuestion.id)
                                                                        }}
                                                                    >
                                                                        <SparklesIcon className="w-5 h-5"/>
                                                                        Fine Tune Grading
                                                                    </button>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="flex justify-between items-center mb-4">
                                                                <button
                                                                    className="px-4 py-2 flex items-center gap-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                                                                    onClick={() => handleEditAIFeedback(subQuestion.id)}
                                                                >
                                                                    <BrainIcon className="w-5 h-5"/>
                                                                    Add Feedback
                                                                </button>

                                                                {/* Fine Tune Grading Button when no feedback */}
                                                                <button
                                                                    disabled={isDisabledGrading}
                                                                    className={`px-4 py-2 flex items-center gap-2 ${
                                                                        'text-amber-600 hover:bg-amber-50'
                                                                    } rounded-lg transition-colors ${isDisabledGrading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                    onClick={() => {
                                                                        handleRegradeClick(subQuestion.id)
                                                                    }}
                                                                >
                                                                    <SparklesIcon className="w-5 h-5"/>
                                                                    Fine Tune Grading
                                                                </button>
                                                            </div>
                                                        )
                                                    )}


                                                    {getRR(subQuestion.id) && (
                                                        <div
                                                            className="mt-4 p-4 bg-gradient-to-r from-[rgba(228,177,121,0.08)] to-[rgba(228,177,121,0.04)] border-l-4 border-[#e4b17a] rounded-lg transition-all duration-200 hover:shadow-sm">
                                                            <div className="flex justify-between items-start">
                                                                <h4 className="font-medium text-[#8c6b4a] mb-2 flex items-center gap-2">
                                                                    <ExclamationCircleIcon
                                                                        className="w-5 h-5 text-[#e4b17a]"/>
                                                                    Regrade Request
                                                                </h4>
                                                                <button
                                                                    className="px-3 py-1.5 bg-[#e4b17a] hover:bg-[#c99b69] text-white font-medium rounded-md flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow"
                                                                    onClick={() => handleRegradeReq(subQuestion.id)}
                                                                >
                                                                    <XMarkIcon className="w-4 h-4"/>
                                                                    Dismiss
                                                                </button>
                                                            </div>
                                                            <p className="text-[#a17c57]">{getRRText(subQuestion.id)}</p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                    </div>
                                );
                            })}
                        </div>
                    ))}


                </div>
            </div>

            {isRegradeOpen && (
                <AIRegrade
                    subQuestion={examPaper
                        .flatMap(q => q.subQuestions)
                        .find(sq => sq.id === activeSubQuestionId)}
                    isEssay={assignment?.type === 'essay'}
                    onClose={handleRegradeClose}
                    onSubmit={handleRegradeSubmit}
                />
            )}
            <ConfirmationModal
                isOpen={isConfirmationOpen}
                message="Are you sure you want to regrade this subquestion for all students with the new rubric and grading instructions?"
                onClose={() => setIsConfirmationOpen(false)}
                onConfirm={handleConfirmRegrade}
            >
                <p>This action cannot be undone.</p>
            </ConfirmationModal>

            <ConfirmationModal
                isOpen={isStudentAssignmentConfirmationOpen}
                message={`Assigning to ${newStudentAssignment?.full_name} (ID: ${newStudentAssignment?.id}) will unassign any submission that may be currently assigned to them.`}
                onClose={() => setIsStudentAssignmentConfirmationOpen(false)}
                onConfirm={handleConfirmStudentAssignment}
            >
                <p>Are you sure you want to proceed?</p>
            </ConfirmationModal>

            {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <p>Regrading in progress...</p>
                        <p>Expected wait time: 30 seconds</p>
                        {/* Add your loading animation here */}
                    </div>
                </div>
            )}

            {/*{showConnectionErrorPopup && (*/}
            {/*    <>*/}
            {/*        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded">*/}
            {/*            Connection error, reconnecting ...*/}
            {/*        </div>*/}
            {/*    </>*/}
            {/*)}*/}

            {nextRegrade != -1 ? (
                <button
                    className="fixed h-[40px] w-[40px] right-[3%] bottom-[5%] z-40
            rounded-full border-[1px] border-black bg-white"
                    onClick={handleScroll}
                >
                    <i className="fas fa-chevron-down m-auto"/>
                </button>
            ) : null}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-800">Delete Student Submissions</h2>
                            <button
                                className="text-gray-400 hover:text-gray-600"
                                onClick={handleCloseDeleteModal}
                            >
                                <XMarkIcon className="h-6 w-6"/>
                            </button>
                        </div>

                        <div className="my-4">
                            <div className="flex items-center justify-center mb-4 text-red-500">
                                <div className="bg-red-100 p-3 rounded-full">
                                    <TrashIcon className="h-8 w-8"/>
                                </div>
                            </div>

                            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                                <p className="text-red-700">
                                    <strong>Warning:</strong> This action cannot be undone.
                                </p>
                                <p className="text-red-700 mt-2">
                                    This will permanently delete <strong>all submissions</strong> for this student from
                                    this assignment, including all grades, comments, and associated data.
                                </p>
                            </div>

                            <p className="text-gray-600 mb-4">Are you sure you want to delete all submissions for this
                                student?</p>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                                onClick={handleCloseDeleteModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                                onClick={confirmDeleteSubmission}
                            >
                                Delete All
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Navigation Legend */}
            {showNavTips && (
                <div
                    className="fixed bottom-10 right-5 bg-white shadow-lg rounded-xl border border-orange-200 p-4 max-w-xs animate-fade-in z-50">
                    <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-800">Navigation Tips</h4>
                        <button
                            className="text-gray-400 hover:text-gray-600"
                            onClick={() => setShowNavTips(false)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24"
                                 stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded">
                                <ArrowLeft size={18} className="text-gray-600"/>
                            </div>
                            <span className="text-gray-600">Previous submission</span>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded">
                                <ArrowRight size={18} className="text-gray-600"/>
                            </div>
                            <span className="text-gray-600">Next submission</span>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded">
                                <ArrowUp size={18} className="text-gray-600"/>
                            </div>
                            <span className="text-gray-600">Scroll to top</span>
                        </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-500">
                        Press these arrow keys on your keyboard for quick navigation
                    </div>
                </div>
            )}
        </div>
    );
};

export default TDetails;